import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";
import StarlightTwinkle from "@/assets/certificates/starlight-twinkle.png";
import CornerImage from "@/assets/certificates/syep-certificate-corner-graphic.png";
import ProximaNovaReg from "@/assets/fonts/proximanova_regular.ttf";
import ProximaNovaBold from "@/assets/fonts/proximanova_bold.otf";
import Nunito from "@/assets/fonts/Nunito-Bold.ttf";
import Mulish from "@/assets/fonts/Mulish-Medium.ttf";
import MulishBold from "@/assets/fonts/Mulish-Bold.ttf";

Font.register({ family: "ProximaNova", src: ProximaNovaReg });
Font.register({
  family: "ProximaNovaBold",
  src: ProximaNovaBold,
  fontWeight: "bold",
});
Font.register({ family: "Nunito", src: Nunito, fontWeight: "bold" });
Font.register({ family: "Mulish", src: Mulish, fontWeight: 400 });
Font.register({ family: "MulishBold", src: MulishBold, fontWeight: 700 });

const styles = StyleSheet.create({
  page: {
    flexDirection: "row",
    backgroundColor: "#fff",
  },
  cornerImage: {
    position: "absolute",
    right: -25,
    top: -25,
    width: 190,
    height: 190,
  },
  container: {
    flex: 1,
    backgroundColor: "#fff",
    padding: 20,
  },
  body: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  logo: {
    marginBottom: 20,
  },
  header: {
    flexDirection: "row",
  },
  title: {
    fontSize: 18,
    marginBottom: 20,
    marginTop: 20,
    fontFamily: "Mulish",
  },
  name: {
    fontSize: 42,
    fontWeight: 700,
    marginBottom: 20,
    color: "#8420B4",
    fontFamily: "Nunito",
  },
  text: {
    fontSize: 14,
    marginBottom: 5,
    fontFamily: "Mulish",
  },
  textDescription: {
    fontSize: 14,
    marginBottom: 5,
    fontFamily: "Mulish",
    maxWidth: 505,
  },
  skillsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 10,
    maxWidth: 460,
  },
  skillTag: {
    backgroundColor: "#E2B6FF",
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 8,
    fontSize: 10,
    color: "#8420B4",
    fontFamily: "MulishBold",
  },
  footer: {
    position: "absolute",
    bottom: 20,
    marginTop: 20,
    fontSize: 12,
  },
  inline: {
    flexDirection: "row",
    width: 380,
    marginRight: 55,
  },
  footerTextTop: {
    minWidth: 135,
    marginRight: 35,
    fontFamily: "Mulish",
    fontSize: 16,
  },
  footerText: {
    minWidth: 135,
    marginRight: 35,
    fontFamily: "Mulish",
    fontSize: 12,
  },
  footerBar: {
    minWidth: 135,
    height: 2,
    marginRight: 35,
    backgroundColor: "black",
    marginTop: 10,
    marginBottom: 10,
  },
});

// PDF document component
const parseMetadata = (raw) => {
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }
  return raw;
};

const CertificatePdf = ({ award, completionDate, currentUser }) => {
  const config = award.certificateConfig || {};
  const metadata = parseMetadata(config.metadata);
  const theme = config.theme || "dycd";
  const backgroundColor = metadata.backgroundColor || "#FFD100";
  const estimatedCertTime = metadata.estimatedTrainingHours;
  const signatories = metadata.signatories || [];
  const skills = (config.skills || []).map((s) => s.skill);
  const logoUrl = config.logoImage?.url;
  const badgeUrl = config.badgeImage?.url || award.image?.url;

  return (
    <Document>
      <Page size="LETTER" orientation="landscape" style={styles.page}>
        <View style={{ flex: 1, padding: 25, backgroundColor }}>
          <View style={styles.container}>
            <Image src={CornerImage} style={styles.cornerImage} />

            {logoUrl && (
              <View style={styles.logo}>
                <Image src={logoUrl} style={{ maxWidth: 240, maxHeight: 77 }} />
              </View>
            )}

            <View style={styles.header}>
              <Text style={styles.title}>THIS CERTIFICATE IS AWARDED TO</Text>
              <Image
                src={StarlightTwinkle}
                style={{ width: "36px", height: "36px", marginTop: -10 }}
              />
            </View>

            <View>
              <Text style={styles.name}>
                {currentUser.firstName} {currentUser.lastName}
              </Text>

              <>
                {config.descriptionText && (
                  <Text style={[styles.text, { marginBottom: 20 }]}>
                    {config.descriptionText}
                  </Text>
                )}
              </>
            </View>

            {skills.length > 0 && (
              <>
                <View style={styles.body}>
                  <View style={styles.skillsContainer}>
                    {skills.map((skill, index) => (
                      <Text key={index} style={styles.skillTag}>
                        {skill}
                      </Text>
                    ))}
                  </View>
                </View>
              </>
            )}

            <View style={styles.footer}>
              <View style={styles.inline}>
                <Text style={styles.footerTextTop}>{completionDate}</Text>
                {estimatedCertTime && (
                  <Text style={styles.footerTextTop}>
                    {estimatedCertTime} hours
                  </Text>
                )}
                {signatories.map((sig, i) => (
                  <Text key={i} style={styles.footerTextTop}>
                    {sig.name}
                  </Text>
                ))}
              </View>
              <View style={styles.inline}>
                <View style={styles.footerBar} />
                {estimatedCertTime && <View style={styles.footerBar} />}
                {signatories.map((_, i) => (
                  <View key={i} style={styles.footerBar} />
                ))}
              </View>
              <View style={styles.inline}>
                <Text style={styles.footerText}>Date Awarded</Text>
                {estimatedCertTime && (
                  <Text style={styles.footerText}>Training Length</Text>
                )}
                {signatories.map((sig, i) => (
                  <Text key={i} style={styles.footerText}>
                    {sig.title}
                  </Text>
                ))}
              </View>
            </View>
          </View>

          {badgeUrl && (
            <Image
              src={badgeUrl}
              style={{
                width: 260,
                height: 260,
                padding: 20,
                position: "absolute",
                bottom: 20,
                right: 20,
              }}
            />
          )}
        </View>
      </Page>
    </Document>
  );
};

export default CertificatePdf;
